import {
  type Account,
  type AccountModuleVersion,
  type SourceAccount,
  type RestoreAccount,
} from '@repo/api-gw-sdk';
import semver from 'semver';

import { isRestoreAccount, isSourceAccount } from './accounts';

export const extractRoleArn = (roleArn: string) => {
  const roleParts = roleArn.split(/:|\//).reverse();
  const role = roleParts[0];
  const accountId = roleParts[2];
  return { role, accountId };
};

export const getAccountInstalledVersion = (
  account: Account | SourceAccount | RestoreAccount
) =>
  isSourceAccount(account) || isRestoreAccount(account)
    ? account.version?.installed
    : account.installedVersion;

export const getAccountLatestVersion = (
  account: Account | SourceAccount | RestoreAccount
) =>
  isSourceAccount(account) || isRestoreAccount(account)
    ? account.version?.latest
    : account.latestVersion;

// Azure optional capabilities (source capabilities and restore network resource groups) are written as separate
// internal-RG tags that do NOT bump the onboarding version. A stable signature
// of them lets the sync flow tell a capability-only re-run apart from a no-op,
// so re-running while already on the latest version is recognised as a real
// change instead of triggering a false "no version change detected" error.
export const getAzureOptionalCapabilitiesSignature = (
  account: Account | SourceAccount | RestoreAccount
) => {
  const capabilities = isSourceAccount(account)
    ? account.sourceAccountAttributes?.azure?.optionalCapabilities
    : undefined;
  const networkResourceGroups = isRestoreAccount(account)
    ? [
        ...(account.restoreAccountAttributes?.azure?.optionalCapabilities
          ?.networkResourceGroups ?? []),
      ]
        .sort()
        .join(',')
    : '';
  return `${capabilities?.enableAzureFilesBackup ?? false}|${capabilities?.enableRestorePointConversion ?? false}|${networkResourceGroups}`;
};

export const isAccountRoleOld = (
  account: Account | SourceAccount | RestoreAccount
) => {
  const installedVersion = getAccountInstalledVersion(account);
  const latestVersion = getAccountLatestVersion(account);
  return (
    !!latestVersion && semver.lt(installedVersion || '1.0.0', latestVersion)
  );
};

export const isAccountRoleMajorMinorVersionOld = (
  account: Account | SourceAccount | RestoreAccount
) => {
  const installedVersion = getAccountInstalledVersion(account);
  const latestVersion = getAccountLatestVersion(account);
  return (
    !!latestVersion &&
    isMajorMinorLess(installedVersion || '1.0.0', latestVersion)
  );
};

// Onboarding modules release a patch for changes customers need not act on, so only a major or minor bump is
// surfaced; a module with no installed version Eon can attribute to it always needs the update.
export const isModuleUpdateAvailable = (module: AccountModuleVersion) =>
  module.needsUpdate &&
  (!module.installed ||
    !semver.valid(module.installed) ||
    isMajorMinorLess(module.installed, module.latest));

function isMajorMinorLess(a: string, b: string) {
  const normalized = (v: string) => `${semver.major(v)}.${semver.minor(v)}.0`;
  return semver.lt(normalized(a), normalized(b));
}
